import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SiteDetailPage from '@/components/sites/SiteDetailPage';
import { SitesService, Site } from '@/services/sitesService';
import { Loader2 } from 'lucide-react';

/**
 * Page dynamique pour afficher les détails d'un site basé sur les données Supabase
 */
const DynamicSitePage: React.FC = () => {
  const { siteName, city } = useParams<{ siteName: string; city: string }>();
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSite = async () => {
      if (!siteName || !city) {
        setError('Paramètres de site manquants');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const siteData = await SitesService.getSiteByNameAndCity(siteName, city);
        
        if (siteData) {
          setSite(siteData);
        } else {
          setError('Site non trouvé');
        }
      } catch (err) {
        console.error('Erreur lors du chargement du site:', err);
        setError('Erreur lors du chargement du site');
      } finally {
        setLoading(false);
      }
    };

    loadSite();
  }, [siteName, city]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3 text-fabre-green">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="text-lg">Chargement du site...</span>
        </div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Site non trouvé</h1>
          <p className="text-gray-600 mb-8">{error || 'Le site demandé n\'existe pas.'}</p>
          <a href="/sites" className="text-fabre-green hover:text-fabre-darkgreen underline">
            Voir tous nos sites
          </a>
        </div>
      </div>
    );
  }

  return (
    <SiteDetailPage
      title={`${site.name} - ${site.city} (${site.postal_code})`}
      description={site.description || `${site.name} à ${site.city} - Services de recyclage et valorisation des déchets.`}
      proximityInfo={site.description || `Centre de recyclage situé à ${site.city}, spécialisé dans la valorisation des déchets et des métaux.`}
      address={site.address}
      postalCode={site.postal_code}
      city={site.city}
      phone={site.phone}
      email={site.email}
      website={site.website}
      services={site.services || []}
      coordinates={site.latitude && site.longitude ? { lat: site.latitude, lng: site.longitude } : undefined}
      isMiniSite={true}
    />
  );
};

export default DynamicSitePage;